import { useCallback, useState } from 'react';

/**
 * Drop-in replacement for usehooks-ts useElementSize that works in
 * dialog-mounted components. Uses a callback ref so the ResizeObserver
 * attaches when the element actually mounts (not in a useEffect that
 * may fire before the dialog content is in the DOM).
 */
export function useResizeObserver<T extends HTMLElement = HTMLElement>(): [
  (node: T | null) => void,
  { width: number; height: number },
] {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [observer] = useState(() =>
    typeof window !== 'undefined' && 'ResizeObserver' in window
      ? new ResizeObserver(([entry]) => {
          if (entry) {
            setSize({
              width: entry.contentRect.width,
              height: entry.contentRect.height,
            });
          }
        })
      : null,
  );

  const ref = useCallback(
    (node: T | null) => {
      observer?.disconnect();
      if (node) {
        observer?.observe(node);
      }
    },
    [observer],
  );

  return [ref, size];
}
